import { useMutation, useQueryClient } from "react-query";
import http from "shared/http";
import { accessTokenName } from "shared/config";
import { removeCookie } from "shared/helpers";
import { clearAppState } from "shared/appStateStorage";

export const useLogout = () => {
  const queryClient = useQueryClient();

  return async () => {
    try {
      await http.postAsync("auth/logout");
    } catch {
      // proceed even if the server call fails
    }

    removeCookie(accessTokenName);
    queryClient.clear();
    await clearAppState();
    window.location.href = "/";
  };
};

export interface RequestPasswordlessCodeRequest {
  email: string;
}

export interface VerifyPasswordlessCodeRequest {
  email: string;
  code: string;
}

export const useRequestPasswordlessCodeMutation = () => {
  return useMutation(async (request: RequestPasswordlessCodeRequest) => {
    await http.postAsync("auth/passwordless/request", request);
  });
};

export interface VerifyPasswordlessCodeResponse {
  token: string;
  expires: string;
}

export const useVerifyPasswordlessCodeMutation = () => {
  return useMutation(async (request: VerifyPasswordlessCodeRequest) => {
    const response = await http.postAsync("auth/passwordless/verify", request);
    return response as VerifyPasswordlessCodeResponse;
  });
};

export interface TestAccountLoginRequest {
  email: string;
  password: string;
}

export const useTestAccountLoginMutation = () => {
  return useMutation(async (request: TestAccountLoginRequest) => {
    const response = await http.postAsync("auth/test-account/login", request);
    return response as VerifyPasswordlessCodeResponse;
  });
};

export interface SsoRegisterRequest {
  sessionId: string;
  requestType: string;
  agencyCode?: string;
  externalSystemId?: string;
}

export const useSsoRegisterMutation = () => {
  return useMutation(async (request: SsoRegisterRequest) => {
    await http.postAsync("auth/sso-register", request);
  });
};

export interface RedeemInviteResponse {
  token: string;
  expires: string;
}

export const useRedeemInviteMutation = () => {
  return useMutation(async (code: string) => {
    const response = await http.postAsync("auth/invite/redeem", { code });
    return response as RedeemInviteResponse;
  });
};
